import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { subDays } from "date-fns";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import type IEnduserStatsOverview from "../dataTypes/IEnduserStatsOverview";
import type IProjectGroupCompletionItem from "../dataTypes/IProjectGroupCompletionItem";
import type IProjectGroupsCompletionItem from "../dataTypes/IProjectGroupsCompletionItem";
import type ISessionDetailForGivenProject from "../dataTypes/ISessionDetailForGivenProject";

export interface EnduserDateRange {
  startDate: string | null; // ISO string for Redux serialization
  endDate: string | null;
}

interface IEnduserSession {
  id: number;
  createdAt: string;
  projectId: number | null;
  projectName: string | null;
  projectGroupId: number | null;
  projectGroupName: string | null;
  duration?: number;
  isCompleted?: boolean;
  score?: number | null;
  scoreMax?: number | null;
  progressionPercentage?: number | null;
}

interface IEnduserStatsState {
  projectGroupsCompletion: IProjectGroupsCompletionItem[];
  currentProjectGroupCompletion: IProjectGroupCompletionItem[];
  sessionDetailForGivenProject: ISessionDetailForGivenProject;
  enduserStatsOverview: IEnduserStatsOverview;
  allSessions: IEnduserSession[];
  dateRange: EnduserDateRange;
  // Cache metadata
  cachedForUserId: number | null;
  lastFetchTimestamp: number | null;
  // Module cache metadata (per buildId)
  cachedModulesForBuildId: number | null;
}
const initialState: IEnduserStatsState = {
  projectGroupsCompletion: [],
  currentProjectGroupCompletion: [],
  sessionDetailForGivenProject: {
    isProjectScorable: false,
    sessions: [],
  },
  enduserStatsOverview: {
    numberOfFinishedProjectGroups: 0,
    numberOfSucceededProjectGroups: 0,
    numberOfRemainingProjectGroups: 0,
    overallSuccessRate: 0,
    sessionsForTheTwelveLastMonths: [],
    successRatePerProjectGroupSortedByLatestSession: [],
  },
  allSessions: [],
  dateRange: {
    startDate: subDays(new Date(), 29).toISOString(),
    endDate: new Date().toISOString(),
  },
  cachedForUserId: null,
  lastFetchTimestamp: null,
  cachedModulesForBuildId: null,
};

export const fetchProjectGroupsCompletion = createAsyncThunk(
  "enduserStats/fetchProjectGroupsCompletion",

  async ({ limit = -1 }: { limit?: number } & IAppLoaderAction) => {
    return await axios
      .get(`${apiUrls.stats.enduserProjectGroupsCompletionSortedByLatestSession}?limit=${limit}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
        // Never resolve to undefined — consumers do unguarded `.length/.filter/.find`
        // on this field, so a failed request must degrade to an empty list, not a crash.
        return [];
      });
  },
);

export const fetchCurrentProjectGroupCompletion = createAsyncThunk(
  "enduserStats/fetchCurrentProjectGroupCompletion",

  async ({ buildId }: { buildId: number } & IAppLoaderAction) => {
    return await axios
      .get(`${apiUrls.stats.enduserProjectGroupCompletion}?build=${buildId}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const fetchEnduserSessionDetailsForGivenProject = createAsyncThunk(
  "enduserStats/fetchEnduserSessionDetailsForGivenProject",

  async ({ projectId }: { projectId: number } & IAppLoaderAction) => {
    return await axios
      .get(`${apiUrls.stats.enduserSessionDetailsForGivenProject}?project=${projectId}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const fetchEnduserStatsOverview = createAsyncThunk(
  "enduserStats/fetchEnduserStatsOverview",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    // Cursed bugs calls for cursed solutions.
    // Undefined jwt will only happen here, only once, when F5'ing the dashboard
    if (!axios.defaults.headers.common["Authorization"]) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
        "wixar.auth.jwt",
      )}`;
    }
    return await axios
      .get(`${apiUrls.stats.enduserStatsOverview}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const fetchEnduserAllSessions = createAsyncThunk(
  "enduserStats/fetchEnduserAllSessions",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    if (!axios.defaults.headers.common["Authorization"]) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
        "wixar.auth.jwt",
      )}`;
    }
    return await axios
      .get(`${apiUrls.stats.enduserAllSessions}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const enduserStatsSlice = createSlice({
  name: "enduserStats",
  initialState,
  reducers: {
    setEnduserDateRange: (state, action: PayloadAction<EnduserDateRange>) => {
      state.dateRange = action.payload;
    },
    clearEnduserDateRange: (state) => {
      state.dateRange = { startDate: null, endDate: null };
    },
    // Set the cached user ID when data is fetched
    setCachedUserId: (state, action: PayloadAction<number>) => {
      state.cachedForUserId = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    // Invalidate cache (called when user changes)
    invalidateEnduserStatsCache: (state) => {
      state.projectGroupsCompletion = [];
      state.currentProjectGroupCompletion = [];
      state.sessionDetailForGivenProject = { isProjectScorable: false, sessions: [] };
      state.allSessions = [];
      state.cachedForUserId = null;
      state.lastFetchTimestamp = null;
      state.cachedModulesForBuildId = null;
    },
    // Set the cached buildId when modules are fetched
    setCachedModulesBuildId: (state, action: PayloadAction<number>) => {
      state.cachedModulesForBuildId = action.payload;
    },
    // Invalidate modules cache (for visibility refresh)
    invalidateModulesCache: (state) => {
      state.cachedModulesForBuildId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchProjectGroupsCompletion.fulfilled,
      (state: IEnduserStatsState, { payload }) => {
        state.projectGroupsCompletion = payload ?? [];
      },
    );
    builder.addCase(
      fetchCurrentProjectGroupCompletion.fulfilled,
      (state: IEnduserStatsState, { payload }) => {
        state.currentProjectGroupCompletion = payload;
      },
    );
    builder.addCase(
      fetchEnduserSessionDetailsForGivenProject.fulfilled,
      (state: IEnduserStatsState, { payload }) => {
        state.sessionDetailForGivenProject = payload;
      },
    );
    builder.addCase(
      fetchEnduserStatsOverview.fulfilled,
      (state: IEnduserStatsState, { payload }) => {
        if (payload) {
          state.enduserStatsOverview = payload;
        }
      },
    );
    builder.addCase(fetchEnduserAllSessions.fulfilled, (state: IEnduserStatsState, { payload }) => {
      if (payload) {
        state.allSessions = payload;
      }
    });
  },
});

// Actions
export const {
  setEnduserDateRange,
  clearEnduserDateRange,
  setCachedUserId,
  invalidateEnduserStatsCache,
  setCachedModulesBuildId,
  invalidateModulesCache,
} = enduserStatsSlice.actions;

// Selectors
export const getEnduserProjectGroupsCompletion = (state: RootState) =>
  state.enduserStats.projectGroupsCompletion;
export const getEnduserCurrentProjectGroupCompletion = (state: RootState) =>
  state.enduserStats.currentProjectGroupCompletion;
export const getEnduserSessionDetailForGivenProject = (state: RootState) =>
  state.enduserStats.sessionDetailForGivenProject;
export const getEnduserStatsOverview = (state: RootState) =>
  state.enduserStats.enduserStatsOverview;
export const getEnduserAllSessions = (state: RootState) => state.enduserStats.allSessions;
export const selectEnduserDateRange = (state: RootState) => state.enduserStats.dateRange;
export const selectEnduserStatsCachedForUserId = (state: RootState) =>
  state.enduserStats.cachedForUserId;
export const selectCachedModulesForBuildId = (state: RootState) =>
  state.enduserStats.cachedModulesForBuildId;

// Helper to convert ISO strings back to Date objects (memoized to prevent unnecessary re-renders)
export const selectEnduserDateRangeAsDates = createSelector(
  [selectEnduserDateRange],
  (dateRange) => ({
    startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
    endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
  }),
);

export const enduserStatsReducer = enduserStatsSlice.reducer;
